import {
  Color3,
  Mesh,
  MeshBuilder,
  PBRMaterial,
  TransformNode,
  Vector3,
  VertexData,
  type Scene,
} from "@babylonjs/core";
import { hashNoise } from "../../utils/noise";

const FROND_COUNT = 9;
const TRUNK_HEIGHT = 5.5;

/**
 * Procedural placeholder palm tree — the island's single, focal hero prop.
 * Style per docs/Castaway Plan.md (silhouette/palette cues only, nothing
 * reproduced from the reference clipart directly): a curved, ringed trunk;
 * a radial fan of drooping fronds with a dark-base-to-light-tip color
 * gradient; a small coconut cluster at the crown.
 *
 * Placeholder pending the Blender MCP pipeline (unreachable — see plan
 * status) for a higher-fidelity sculpted/rigged version with real per-vertex
 * wind bending; this version approximates wind with a cheap whole-frond sway.
 */
export class PalmTree {
  readonly root: TransformNode;
  /** World-space position of the crown (where fronds/coconuts meet the trunk) — used by EnvironmentDecorator to hang holiday props from the right spot. */
  readonly crownWorldPosition: Vector3;
  private readonly fronds: Mesh[] = [];
  private readonly frondBaseDroopX: number[] = [];
  private windTime = 0;

  constructor(scene: Scene, origin: Vector3 = Vector3.Zero()) {
    this.root = new TransformNode("palmTreeRoot", scene);
    this.root.position = origin;

    const { mesh: trunk, crown } = this.buildTrunk(scene);
    this.crownWorldPosition = origin.add(crown);
    trunk.parent = this.root;

    for (let i = 0; i < FROND_COUNT; i++) {
      const angle = (i / FROND_COUNT) * Math.PI * 2;
      const lengthVariance = 1 + hashNoise(i * 3.3, 7.1) * 0.15;
      const frond = this.buildFrond(scene, `frond${i}`, 3.2 * lengthVariance);
      frond.parent = this.root;
      frond.position = crown;
      frond.rotation.y = angle; // yaw: point radially outward
      const baseDroop = -(Math.PI / 5 + hashNoise(i, 2) * 0.15);
      frond.rotation.x = baseDroop; // pitch, applied after yaw — droops downward
      this.frondBaseDroopX.push(baseDroop);
      this.fronds.push(frond);
    }

    for (const coconut of this.buildCoconutCluster(scene, crown)) {
      coconut.parent = this.root;
    }

    for (const blade of this.buildGrassTuft(scene)) {
      blade.parent = this.root;
    }
  }

  /**
   * Cheap GPU-idle wind: sways each frond rigidly. A real per-vertex wind
   * bend (per docs/Castaway Plan.md's PalmTree.ts note) is deferred to when
   * this becomes a proper rigged/shader-driven asset.
   */
  update(deltaMs: number): void {
    this.windTime += deltaMs * 0.001;
    for (let i = 0; i < this.fronds.length; i++) {
      const sway = Math.sin(this.windTime * 1.3 + i * 1.7) * 0.06;
      this.fronds[i].rotation.x = this.frondBaseDroopX[i] + sway;
    }
  }

  private buildTrunk(scene: Scene): { mesh: Mesh; crown: Vector3 } {
    const segments = 12;
    const path: Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const lean = Math.pow(t, 1.6) * 1.6; // gentle curve, more pronounced near the top
      path.push(new Vector3(lean, t * TRUNK_HEIGHT, 0));
    }

    const trunk = MeshBuilder.CreateTube(
      "palmTrunk",
      {
        path,
        radiusFunction: (i: number) => {
          const t = i / segments;
          const taper = 0.34 * (1 - t) + 0.12 * t;
          const ring = Math.sin(t * Math.PI * 2 * 14) * 0.012; // subtle ridged-ring silhouette
          return Math.max(0.08, taper + ring);
        },
        tessellation: 10,
        cap: Mesh.CAP_ALL,
        updatable: false,
      },
      scene
    );

    const mat = new PBRMaterial("trunkMaterial", scene);
    mat.albedoColor = new Color3(0.52, 0.4, 0.27); // warm tan-brown, per reference
    mat.roughness = 0.85;
    mat.metallic = 0;
    mat.environmentIntensity = 0; // see IslandTerrain's sandMaterial note — flat, matte, direct-light response
    trunk.material = mat;
    trunk.freezeWorldMatrix(); // never moves (Phase 4 perf pass) — unlike the fronds, which sway every frame

    return { mesh: trunk, crown: path[path.length - 1] };
  }

  private buildFrond(scene: Scene, name: string, length: number): Mesh {
    const spineSegments = 10;
    const positions: number[] = [];
    const colors: number[] = [];
    const baseColor = new Color3(0.1, 0.32, 0.12);
    const tipColor = new Color3(0.48, 0.64, 0.24); // lighter, saturated tip — per reference banding

    for (let i = 0; i <= spineSegments; i++) {
      const t = i / spineSegments;
      const droop = Math.pow(t, 1.8) * length * 0.55;
      const x = t * length;
      const y = -droop;
      const width = Math.max(Math.sin(t * Math.PI) * (0.3 - t * 0.06), 0.004);
      positions.push(x, y, width, x, y, -width);

      const c = Color3.Lerp(baseColor, tipColor, t);
      colors.push(c.r, c.g, c.b, 1, c.r, c.g, c.b, 1);
    }

    const indices: number[] = [];
    for (let i = 0; i < spineSegments; i++) {
      const a = i * 2;
      const b = i * 2 + 1;
      const c = (i + 1) * 2;
      const d = (i + 1) * 2 + 1;
      indices.push(a, b, c, b, d, c);
    }

    const normals: number[] = [];
    VertexData.ComputeNormals(positions, indices, normals);

    const vertexData = new VertexData();
    vertexData.positions = positions;
    vertexData.indices = indices;
    vertexData.colors = colors;
    vertexData.normals = normals;

    const mesh = new Mesh(name, scene);
    vertexData.applyToMesh(mesh, true);

    const mat = new PBRMaterial(`${name}Material`, scene);
    mat.albedoColor = Color3.White(); // let vertex-color gradient carry the tint
    mat.roughness = 0.45; // slight sheen — matches the reference's glossier foliage read
    mat.metallic = 0;
    mat.backFaceCulling = false; // thin foliage blades read better double-sided
    mat.twoSidedLighting = true; // ...and need this too, or the backface reads unlit
    mesh.material = mat;

    return mesh;
  }

  private buildCoconutCluster(scene: Scene, crown: Vector3): Mesh[] {
    const mat = new PBRMaterial("coconutMaterial", scene);
    mat.albedoColor = new Color3(0.32, 0.22, 0.14);
    mat.roughness = 0.8;
    mat.metallic = 0;
    mat.environmentIntensity = 0;

    return [0, 1, 2].map((i) => {
      const coconut = MeshBuilder.CreateSphere(`coconut${i}`, { diameter: 0.32, segments: 8 }, scene);
      const angle = (i / 3) * Math.PI * 2;
      coconut.position = crown.add(new Vector3(Math.cos(angle) * 0.18, -0.15, Math.sin(angle) * 0.18));
      coconut.material = mat;
      coconut.freezeWorldMatrix(); // never moves (Phase 4 perf pass)
      return coconut;
    });
  }

  private buildGrassTuft(scene: Scene): Mesh[] {
    const mat = new PBRMaterial("grassMaterial", scene);
    mat.albedoColor = new Color3(0.28, 0.42, 0.16);
    mat.roughness = 0.7;
    mat.metallic = 0;
    mat.environmentIntensity = 0;

    const blades: Mesh[] = [];
    for (let i = 0; i < 7; i++) {
      const blade = MeshBuilder.CreateCylinder(
        `grassBlade${i}`,
        { diameterTop: 0, diameterBottom: 0.05, height: 0.4 + hashNoise(i, 5) * 0.15, tessellation: 4 },
        scene
      );
      const angle = hashNoise(i, 11) * Math.PI * 2;
      const dist = 0.35 + Math.abs(hashNoise(i, 19)) * 0.25;
      blade.position = new Vector3(Math.cos(angle) * dist, 0.2, Math.sin(angle) * dist);
      blade.rotation.z = hashNoise(i, 23) * 0.3;
      blade.material = mat;
      blade.freezeWorldMatrix(); // never moves (Phase 4 perf pass)
      blades.push(blade);
    }
    return blades;
  }

  dispose(): void {
    this.root.dispose();
  }
}
